import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from 'schools/parashari/contexts/AuthContext';
import { LEARNING_CONSENT_VERSION } from 'schools/parashari/constants/jyotishaLearningConsent';
import {
  fetchLearningConsent,
  recordLearningConsent,
} from 'schools/parashari/lib/supabase/learningConsentSync';
import type { LearningConsentState } from 'types/learningConsent';

export type LearningConsentContextValue = LearningConsentState;

const LearningConsentContext = createContext<LearningConsentState | null>(null);

export function LearningConsentProvider({ children }: { children: ReactNode }) {
  const { userId, loading: authLoading } = useAuth();
  const [hasConsent, setHasConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [consentError, setConsentError] = useState('');

  useEffect(() => {
    if (authLoading) {
      setConsentChecked(false);
      setConsentLoading(false);
      setHasConsent(false);
      setConsentError('');
      return;
    }

    if (!userId) {
      setHasConsent(false);
      setConsentChecked(true);
      setConsentLoading(false);
      setConsentError('');
      return;
    }

    let cancelled = false;
    setConsentChecked(false);
    setConsentLoading(true);
    setConsentError('');

    fetchLearningConsent(userId)
      .then((row) => {
        if (cancelled) return;
        setHasConsent(row?.consentVersion === LEARNING_CONSENT_VERSION);
        setConsentChecked(true);
      })
      .catch(() => {
        if (cancelled) return;
        setHasConsent(false);
        setConsentChecked(true);
      })
      .finally(() => {
        if (!cancelled) setConsentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, userId]);

  const acceptConsent = useCallback(async () => {
    if (!userId) return;
    setConsentBusy(true);
    setConsentError('');
    try {
      await recordLearningConsent(userId);
      setHasConsent(true);
      setConsentChecked(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save consent.';
      setConsentError(message);
      throw err;
    } finally {
      setConsentBusy(false);
    }
  }, [userId]);

  const value = useMemo(
    () => ({
      hasConsent,
      consentChecked,
      consentLoading,
      consentBusy,
      consentError,
      acceptConsent,
    }),
    [hasConsent, consentChecked, consentLoading, consentBusy, consentError, acceptConsent],
  );

  return (
    <LearningConsentContext.Provider value={value}>{children}</LearningConsentContext.Provider>
  );
}

export function useLearningConsentContext(): LearningConsentState {
  const ctx = useContext(LearningConsentContext);
  if (!ctx) {
    throw new Error('useLearningConsentContext must be used within LearningConsentProvider');
  }
  return ctx;
}

/** Pure helper for tests and showRequiredEthics gating. */
export function shouldShowRequiredEthics(
  userPresent: boolean,
  consentChecked: boolean,
  consentLoading: boolean,
  hasConsent: boolean,
): boolean {
  return Boolean(userPresent && consentChecked && !consentLoading && !hasConsent);
}
